import { useEffect, useRef, useState } from 'react'
import { CentresManagementScreen } from '../screens/CentresManagementScreen.js'
import { LoginScreen } from '../screens/LoginScreen.js'
import { OverviewScreen } from '../screens/OverviewScreen.js'
import { StudentScreen } from '../screens/StudentScreen.js'
import { useAuthStore } from '../shared/auth-store.js'
import type { Screen } from '../shared/router-store.js'
import { useRouter } from '../shared/router-store.js'
import { LoadingDots } from '../shared/ui/LoadingDots.js'

export function App() {
  const { status, bootstrap } = useAuthStore()
  const { screen } = useRouter()

  // Deferred screen swap so the fade-out animation has time to play before
  // the outgoing screen unmounts — same pattern used in the mini app.
  const [displayScreen, setDisplayScreen] = useState<Screen>(screen)
  const [isExiting, setIsExiting] = useState(false)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only re-run when screen changes, displayScreen is internal derived state
  useEffect(() => {
    if (screen === displayScreen) return
    setIsExiting(true)
    exitTimerRef.current = setTimeout(() => {
      setDisplayScreen(screen)
      setIsExiting(false)
    }, 160)
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current)
    }
  }, [screen])

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <LoadingDots />
      </div>
    )
  }

  if (status !== 'authenticated') {
    return <LoginScreen />
  }

  return (
    <div className={isExiting ? 'animate-fade-out pointer-events-none' : ''}>
      {displayScreen.name === 'student' ? (
        <StudentScreen
          userId={displayScreen.userId}
          firstName={displayScreen.firstName}
        />
      ) : displayScreen.name === 'centres-management' ? (
        <CentresManagementScreen />
      ) : (
        <OverviewScreen />
      )}
    </div>
  )
}
